import { computed, reactive, ref, watch } from 'vue';
import dayjs from '@/dayjs';
import type { AppointmentEntryAttributes, Availability } from '@/types';
import { canCheckAvailability } from './availabilities';
import type { FormData } from './index';
import { useSeriesAvailabilities } from './series-availabilities';

// "Add more dates" mode: the first date is `formData.startAt` (shown in the Date & time card),
// the extra dates live in `additionalDates` (shown in the "Add more dates" card as Date 2, 3, ...).
// On submit all of them go to the createAppointments mutation as independent appointments.

export const FIRST_DATE_ID = 'first';

export type SeriesDate = {
  id: string;
  startAt: string;
};

export type SeriesDateStatus =
  | 'unknown'
  | 'available'
  | 'unavailable'
  | 'error';

let uid = 0;
const nextId = () => `series-date-${(uid += 1)}`;

export const useAppointmentSeries = (formData: FormData) => {
  const additionalDates = reactive<SeriesDate[]>([]);

  // "Series mode" is now derived: the user is on the Custom date option (no rrule)
  // and has added at least one extra date. A single custom date is a plain appointment.
  const isSeriesMode = computed(
    () => additionalDates.length > 0 && !formData.rrule,
  );

  // The date the calendar edits in preview mode: FIRST_DATE_ID or an additional date's id.
  const activeDateId = ref<string>(FIRST_DATE_ID);

  // Per-date errors returned by the createAppointments mutation, keyed by date id.
  const submitErrors = reactive<Record<string, string[]>>({});

  const {
    isFetchingAvailabilities,
    availabilities,
    fetchAvailabilities,
    resetAvailabilities,
  } = useSeriesAvailabilities();

  const clearSubmitErrors = () => {
    Object.keys(submitErrors).forEach((key) => {
      delete submitErrors[key];
    });
  };

  // The first date plus the extra dates, in order. Drives entries, availability and preview.
  const seriesDates = computed<SeriesDate[]>(() => [
    { id: FIRST_DATE_ID, startAt: formData.startAt },
    ...additionalDates,
  ]);

  const count = computed(() => seriesDates.value.length);

  // The entries sent to both the `availabilities` query and the `createAppointments` mutation.
  // Client and treatment (partsAttributes) are shared across every entry.
  const entries = computed<AppointmentEntryAttributes[]>(() =>
    seriesDates.value.map((date) => ({
      startAt: date.startAt,
      partsAttributes: formData.partsAttributes,
    })),
  );

  // The availability result for a date id (positionally aligned with seriesDates).
  const availabilityByDateId = computed<
    Record<string, Availability | undefined>
  >(() => {
    const map: Record<string, Availability | undefined> = {};
    seriesDates.value.forEach((date, index) => {
      map[date.id] = availabilities.value[index];
    });
    return map;
  });

  const statusForDate = (id: string): SeriesDateStatus => {
    if (submitErrors[id]?.length) {
      return 'error';
    }
    const availability = availabilityByDateId.value[id];
    if (!availability) {
      return 'unknown';
    }
    return availability.available ? 'available' : 'unavailable';
  };

  const setDateStartAt = (id: string, startAt: string) => {
    if (id === FIRST_DATE_ID) {
      formData.startAt = startAt;
    } else {
      const date = additionalDates.find((entry) => entry.id === id);
      if (date) {
        date.startAt = startAt;
      }
    }
    // Editing a date invalidates its previous submit error.
    delete submitErrors[id];
  };

  const addDate = () => {
    // Default the new date to a week after the last date, keeping the same time of day.
    const dates = seriesDates.value;
    const lastStartAt = dates[dates.length - 1]?.startAt || formData.startAt;
    const id = nextId();
    additionalDates.push({
      id,
      startAt: dayjs(lastStartAt).add(1, 'week').format(),
    });
    activeDateId.value = id;
  };

  // Promoting the next date into the first slot; the first date is
  // formData.startAt rather than an entry in additionalDates.
  const promoteNextDateToFirst = () => {
    const nextDate = additionalDates.shift();
    if (nextDate) {
      formData.startAt = nextDate.startAt;
      delete submitErrors[nextDate.id];
    }
    activeDateId.value = FIRST_DATE_ID;
  };

  const removeDate = (id: string) => {
    delete submitErrors[id];

    if (id === FIRST_DATE_ID) {
      promoteNextDateToFirst();
      return;
    }

    const index = additionalDates.findIndex((date) => date.id === id);
    if (index === -1) {
      return;
    }

    // Stay on an adjacent date after removing this one: the previous date, or
    // the first date when this was the first extra.
    const adjacentDateId =
      index > 0 ? additionalDates[index - 1].id : FIRST_DATE_ID;
    additionalDates.splice(index, 1);
    if (activeDateId.value === id) {
      activeDateId.value = adjacentDateId;
    }
  };

  const setActiveDate = (id: string) => {
    activeDateId.value = id;
  };

  // The start time of the currently active date, with a setter used by preview-mode calendar clicks.
  const activeStartAt = computed<string>({
    get() {
      if (activeDateId.value === FIRST_DATE_ID) {
        return formData.startAt;
      }
      return (
        additionalDates.find((entry) => entry.id === activeDateId.value)
          ?.startAt || formData.startAt
      );
    },
    set(startAt) {
      setDateStartAt(activeDateId.value, startAt);
    },
  });

  const checkSeriesAvailabilities = (debounce = 0) => {
    if (!isSeriesMode.value || !canCheckAvailability(formData)) {
      return Promise.resolve<Availability[]>([]);
    }
    return fetchAvailabilities(entries.value, formData.locationId, debounce);
  };

  // Clear the extra dates, e.g. when switching to the "Repeat appointment" option.
  const resetDates = () => {
    additionalDates.splice(0);
    activeDateId.value = FIRST_DATE_ID;
    clearSubmitErrors();
    resetAvailabilities();
  };

  // Refetch availabilities whenever the dates or the shared treatment change.
  // `entries` already embeds formData.partsAttributes, so a deep watch on it
  // covers both the dates and the treatment.
  watch(
    entries,
    () => {
      if (isSeriesMode.value) {
        checkSeriesAvailabilities(500);
      }
    },
    { deep: true },
  );

  const setSubmitErrors = (errors: Record<string, string[]>) => {
    clearSubmitErrors();
    Object.assign(submitErrors, errors);
  };

  return {
    isSeriesMode,
    additionalDates,
    seriesDates,
    activeDateId,
    entries,
    count,
    submitErrors,
    availabilityByDateId,
    isFetchingAvailabilities,
    activeStartAt,
    statusForDate,
    setDateStartAt,
    addDate,
    removeDate,
    setActiveDate,
    checkSeriesAvailabilities,
    setSubmitErrors,
    resetDates,
  };
};
